import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DataService } from '../data-services/data.service';

export interface IExpenseMatchLink {
  id?: string;
  _id?: string;
  organizationId: string;

  importedTransactionId: string;
  importedTransactionModel: 'staged_transaction' | 'transaction';
  importedAmount: number;
  importedDate: string;
  importedDescription?: string;
  importedPayee?: string;

  checkRequisitionId: string;
  requisitionAmount: number;
  requisitionDate?: string;
  requisitionPayee?: string;

  matchStatus: 'pending_review' | 'confirmed' | 'rejected' | 'keep_separate';
  matchScore: number;
  matchReason?: string;
  matchBasis?: Array<{ signal: string; weight: number; detail: string }>;

  dateOfRecord?: string;

  confirmedBy?: string;
  confirmedByName?: string;
  confirmedDate?: string;
  confirmationNote?: string;

  rejectedBy?: string;
  rejectedByName?: string;
  rejectedDate?: string;
  rejectionReason?: string;

  excludeImportedFromReporting?: boolean;
  detectedAt?: string;

  // ── Manual override tracking ──────────────────────────────────────────────
  matchMethod?: 'automatic' | 'manual';
  matchScoreAtConfirmation?: number;
  originalSuggestedTransactionId?: string;
  originalSuggestedTransactionModel?: 'staged_transaction' | 'transaction';
  originalSuggestedCheckRequisitionId?: string;

  documentation?: IExpenseMatchDocument[];

  actionHistory?: Array<{
    action: string;
    performedByName?: string;
    performedAt: string;
    note?: string;
  }>;
}

export interface IExpenseMatchDocument {
  title: string;
  url: string;
  status?: string;
  createdDate?: string;
  createdBy?: string;
}

export interface IExpenseMatchView {
  link: IExpenseMatchLink;
  checkRequisition: any;
  importedTransaction: any;
}

export interface IExpenseMatchCandidatesResult {
  items: IExpenseMatchLink[];
  total: number;
}

export type CandidateMatchStatus = 'available' | 'pending_review' | 'confirmed';

export interface ITransactionCandidate {
  id: string;
  model: 'staged_transaction' | 'transaction';
  date: string;
  description?: string;
  checkNumber?: string | null;
  amount: number;
  account?: string | null;
  status?: string;
  matchStatus: CandidateMatchStatus;
  isCurrentSelection: boolean;
}

export interface IFundsRequestCandidate {
  id: string;
  date: string;
  payee?: string;
  description?: string;
  amount: number;
  checkNumber?: string | null;
  status?: string;
  matchStatus: CandidateMatchStatus;
  isCurrentSelection: boolean;
}

export interface ICandidateSearchResult<T> {
  items: T[];
  total: number;
}

export interface IMatchPreview {
  score: number;
  basis: Array<{ signal: string; weight: number; detail: string }>;
  reason: string;
}

export interface IConfirmMatchOptions {
  note?: string;
  transactionId?: string;
  transactionModel?: 'staged_transaction' | 'transaction';
  checkRequisitionId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExpenseMatchService extends DataService<IExpenseMatchLink> implements OnInit {
  protected collectionname = 'expense-match';

  // Shared across every injector (this service is providedIn: 'root') so the
  // sidebar badge — a separate component instance that only loads once per
  // navigation — updates whenever confirm/reject/keepSeparate/unlink
  // succeeds anywhere in the app, without either side needing to know about
  // the other.
  private pendingCountSubject = new BehaviorSubject<number>(0);
  readonly pendingCount$ = this.pendingCountSubject.asObservable();

  ngOnInit(): void {}

  getPendingCount(): Observable<{ count: number }> {
    const url = `${this.baseUri}/expense-match/pending-count`;
    return this.http
      .get<{ count: number }>(url, { headers: this.headers })
      .pipe(
        tap((res) => this.pendingCountSubject.next(res.count)),
        catchError(this.errorMgmt)
      );
  }

  /** Re-fetch the pending count and push it to every pendingCount$ subscriber (e.g. the sidebar badge). */
  refreshPendingCount(): void {
    this.getPendingCount().subscribe();
  }

  getCandidates(page = 0, size = 20): Observable<IExpenseMatchCandidatesResult> {
    const url = `${this.baseUri}/expense-match/candidates?page=${page}&size=${size}`;
    return this.http
      .get<IExpenseMatchCandidatesResult>(url, { headers: this.headers })
      .pipe(
        map((res) => {
          res.items.forEach((item) => this.setId(item));
          return res;
        }),
        catchError(this.errorMgmt)
      );
  }

  getLinkDetail(linkId: string): Observable<IExpenseMatchView> {
    const url = `${this.baseUri}/expense-match/${linkId}`;
    return this.http
      .get<IExpenseMatchView>(url, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  /**
   * Confirm the suggested match, or a manually-selected pair.
   * Omit transactionId/checkRequisitionId to confirm the suggestion as-is —
   * the backend treats an unchanged pair as an automatic confirmation and a
   * changed one as manual, so the caller never has to decide which it is.
   */
  confirm(linkId: string, options: IConfirmMatchOptions = {}): Observable<{ link: IExpenseMatchLink }> {
    const url = `${this.baseUri}/expense-match/${linkId}/confirm`;
    return this.http
      .post<{ link: IExpenseMatchLink }>(url, options, { headers: this.headers })
      .pipe(
        tap(() => this.refreshPendingCount()),
        catchError(this.errorMgmt)
      );
  }

  reject(linkId: string, reason?: string): Observable<{ link: IExpenseMatchLink }> {
    const url = `${this.baseUri}/expense-match/${linkId}/reject`;
    return this.http
      .post<{ link: IExpenseMatchLink }>(url, { reason }, { headers: this.headers })
      .pipe(
        tap(() => this.refreshPendingCount()),
        catchError(this.errorMgmt)
      );
  }

  keepSeparate(linkId: string, note?: string): Observable<{ link: IExpenseMatchLink }> {
    const url = `${this.baseUri}/expense-match/${linkId}/keep-separate`;
    return this.http
      .post<{ link: IExpenseMatchLink }>(url, { note }, { headers: this.headers })
      .pipe(
        tap(() => this.refreshPendingCount()),
        catchError(this.errorMgmt)
      );
  }

  unlink(linkId: string, reason?: string): Observable<{ link: IExpenseMatchLink }> {
    const url = `${this.baseUri}/expense-match/${linkId}/unlink`;
    return this.http
      .post<{ link: IExpenseMatchLink }>(url, { reason }, { headers: this.headers })
      .pipe(
        tap(() => this.refreshPendingCount()),
        catchError(this.errorMgmt)
      );
  }

  /** Candidate imported transactions for the "Change Transaction" picker. */
  searchTransactionCandidates(
    linkId: string,
    params: { search?: string; mode?: 'suggested' | 'all'; page?: number; size?: number } = {}
  ): Observable<ICandidateSearchResult<ITransactionCandidate>> {
    const url = `${this.baseUri}/expense-match/${linkId}/transaction-candidates`;
    return this.http
      .get<ICandidateSearchResult<ITransactionCandidate>>(url, {
        headers: this.headers,
        params: this.toHttpParams(params),
      })
      .pipe(catchError(this.errorMgmt));
  }

  /** Candidate check requisitions for the "Change Funds Request" picker. */
  searchFundsRequestCandidates(
    linkId: string,
    params: { search?: string; mode?: 'suggested' | 'all'; page?: number; size?: number } = {}
  ): Observable<ICandidateSearchResult<IFundsRequestCandidate>> {
    const url = `${this.baseUri}/expense-match/${linkId}/funds-request-candidates`;
    return this.http
      .get<ICandidateSearchResult<IFundsRequestCandidate>>(url, {
        headers: this.headers,
        params: this.toHttpParams(params),
      })
      .pipe(catchError(this.errorMgmt));
  }

  /** Recompute the match score for a candidate pair without persisting anything. */
  previewScore(
    linkId: string,
    options: { transactionId?: string; transactionModel?: string; checkRequisitionId?: string }
  ): Observable<IMatchPreview> {
    const url = `${this.baseUri}/expense-match/${linkId}/preview`;
    return this.http
      .post<IMatchPreview>(url, options, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  uploadDocument(linkId: string, file: File): Observable<{ link: IExpenseMatchLink }> {
    const url = `${this.baseUri}/expense-match/${linkId}/documents`;
    const formData = new FormData();
    formData.append('file', file, file.name);
    // No Content-Type header — the browser sets the multipart boundary itself.
    return this.http
      .post<{ link: IExpenseMatchLink }>(url, formData)
      .pipe(catchError(this.errorMgmt));
  }

  deleteDocument(linkId: string, key: string): Observable<{ link: IExpenseMatchLink }> {
    const url = `${this.baseUri}/expense-match/${linkId}/documents/${encodeURIComponent(key)}`;
    return this.http
      .delete<{ link: IExpenseMatchLink }>(url, { headers: this.headers })
      .pipe(catchError(this.errorMgmt));
  }

  private toHttpParams(params: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        out[key] = String(params[key]);
      }
    });
    return out;
  }
}
